/**
 * LoginView — Email + password login form with Turnstile.
 */

import type { AweniContext } from '../../../context/AweniContext';
import { createAuthLayout } from '../components/AuthLayout';
import { createTurnstile, type TurnstileInstance } from '../components/TurnstileWidget';
import { validateEmail } from '../validators';
import { login } from '../AuthService';

const TOKEN_KEY = 'learnature_token';

export async function mountLoginView(
  container: HTMLElement,
  ctx: AweniContext,
  opts: { onNavigate: (view: string, params?: Record<string, string>) => void },
): Promise<{ unmount: () => void }> {
  const t = ctx.i18n.t.bind(ctx.i18n);
  const layout = createAuthLayout(ctx);
  layout.setTitle(t('auth_login_title'));
  layout.setSubtitle(t('auth_login_subtitle'));

  let turnstile: TurnstileInstance | null = null;
  let loading = false;

  // -- Form --
  const form = document.createElement('form');
  form.setAttribute('novalidate', '');
  form.autocomplete = 'on';

  // Email
  const emailField = _createField('email', t('auth_email_label'), 'email', 'email');
  form.appendChild(emailField.wrapper);

  // Password
  const pwField = _createPasswordField(t('auth_password_label'), 'current-password');
  form.appendChild(pwField.wrapper);

  // Forgot password link
  const forgotRow = document.createElement('div');
  forgotRow.style.cssText = 'text-align:end;margin:-8px 0 12px;';
  const forgotLink = document.createElement('button');
  forgotLink.type = 'button';
  forgotLink.className = 'auth-link';
  forgotLink.textContent = t('auth_forgot_password');
  forgotLink.addEventListener('click', () => opts.onNavigate('forgot-password'));
  forgotRow.appendChild(forgotLink);
  form.appendChild(forgotRow);

  // Turnstile
  turnstile = await createTurnstile(ctx.theme.isDark);
  form.appendChild(turnstile.container);

  // Submit button
  const submitBtn = document.createElement('button');
  submitBtn.type = 'submit';
  submitBtn.className = 'auth-btn auth-btn-primary';
  submitBtn.innerHTML = `<span>${t('auth_login_button')}</span>`;
  form.appendChild(submitBtn);

  // Sign up link
  const signupRow = document.createElement('div');
  signupRow.className = 'auth-footer-row';
  signupRow.innerHTML = `${t('auth_no_account')} `;
  const signupLink = document.createElement('button');
  signupLink.type = 'button';
  signupLink.className = 'auth-link';
  signupLink.textContent = t('auth_signup_link');
  signupLink.addEventListener('click', () => opts.onNavigate('signup'));
  signupRow.appendChild(signupLink);
  form.appendChild(signupRow);

  // -- Submit handler --
  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    if (loading) return;

    layout.clearMessages();

    const email = emailField.input.value.trim();
    const password = pwField.input.value;

    // Validate
    const emailResult = validateEmail(email);
    if (!emailResult.valid) {
      layout.showError(t(emailResult.errors[0]));
      emailField.input.focus();
      return;
    }
    if (!password) {
      layout.showError(t('auth_error_password_required'));
      pwField.input.focus();
      return;
    }

    loading = true;
    submitBtn.disabled = true;
    submitBtn.innerHTML = `<span class="auth-spinner"></span>`;

    const result = await login(email, password, turnstile?.getToken() || undefined);

    if (result.ok) {
      // Store token
      localStorage.setItem(TOKEN_KEY, result.data.token);
      document.cookie = `${TOKEN_KEY}=${result.data.token};path=/;max-age=86400;SameSite=Lax`;

      // Navigate back to app
      const returnPath = sessionStorage.getItem('aweni_auth_return') || '/home';
      sessionStorage.removeItem('aweni_auth_return');
      ctx.navigate(returnPath);

      // Force auth refresh
      window.dispatchEvent(new CustomEvent('aweni:auth-changed'));
    } else {
      const err = result.error;
      let msg = t(`auth_error_${err.code?.toLowerCase() || 'unknown'}`);
      if (msg.startsWith('auth_error_')) msg = err.error;

      if (err.code === 'EMAIL_NOT_VERIFIED') {
        opts.onNavigate('verify-email', { email });
        return;
      }

      layout.showError(msg);
      turnstile?.reset();
    }

    loading = false;
    submitBtn.disabled = false;
    submitBtn.innerHTML = `<span>${t('auth_login_button')}</span>`;
  });

  layout.card.appendChild(form);
  container.innerHTML = '';
  container.appendChild(layout.wrapper);

  return {
    unmount() {
      turnstile?.destroy();
      container.innerHTML = '';
    },
  };
}

// -- Helpers --

function _createField(
  name: string,
  label: string,
  type: string,
  autocomplete: string,
): { wrapper: HTMLElement; input: HTMLInputElement } {
  const wrapper = document.createElement('div');
  wrapper.className = 'auth-field';

  const labelEl = document.createElement('label');
  labelEl.className = 'auth-label';
  labelEl.textContent = label;
  labelEl.setAttribute('for', `auth-${name}`);

  const input = document.createElement('input');
  input.className = 'auth-input';
  input.type = type;
  input.id = `auth-${name}`;
  input.name = name;
  input.autocomplete = autocomplete as AutoFill;
  input.setAttribute('aria-label', label);

  wrapper.append(labelEl, input);
  return { wrapper, input };
}

function _createPasswordField(
  label: string,
  autocomplete: string,
): { wrapper: HTMLElement; input: HTMLInputElement } {
  const wrapper = document.createElement('div');
  wrapper.className = 'auth-field';

  const labelEl = document.createElement('label');
  labelEl.className = 'auth-label';
  labelEl.textContent = label;
  labelEl.setAttribute('for', 'auth-password');

  const inputWrap = document.createElement('div');
  inputWrap.className = 'auth-input-wrap';

  const input = document.createElement('input');
  input.className = 'auth-input';
  input.type = 'password';
  input.id = 'auth-password';
  input.name = 'password';
  input.autocomplete = autocomplete as AutoFill;
  input.setAttribute('aria-label', label);

  const toggleBtn = document.createElement('button');
  toggleBtn.type = 'button';
  toggleBtn.className = 'auth-toggle-pw';
  toggleBtn.setAttribute('aria-label', 'Toggle password visibility');
  toggleBtn.innerHTML = _eyeIcon();
  toggleBtn.addEventListener('click', () => {
    const show = input.type === 'password';
    input.type = show ? 'text' : 'password';
    toggleBtn.innerHTML = show ? _eyeOffIcon() : _eyeIcon();
  });

  inputWrap.append(input, toggleBtn);
  wrapper.append(labelEl, inputWrap);
  return { wrapper, input };
}

function _eyeIcon(): string {
  return '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>';
}

function _eyeOffIcon(): string {
  return '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>';
}
